import React, { useEffect, useMemo } from 'react';

import { ContractDeal, Entity } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import viewTokens from '@amzn/meridian-tokens/base/icon/view';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';

import { isNil } from 'lodash';
import { ContractSearchModal } from 'src/components/CompareContracts/ContractSearchModal/ContractSearchModal';
import { DataSelector } from 'src/components/CompareContracts/DataSelector/data-selector';
import { TimestampPicker } from 'src/components/CompareContracts/TimestampPicker/TimestampPicker';
import { ROUTES } from 'src/constants/routes';
import { useSearchParam } from 'src/hooks';

export interface IContractSelector {
    side: string;
    deals?: ContractDeal[];
    entities?: Entity[];
    updateId: (id: number) => void;
    updateTimestamp: (timestamp: string | undefined) => void;
}

export const ContractSelector: React.FC<IContractSelector> = (props) => {
    const [id, setId] = useSearchParam(`${props.side}Id`);
    const [timestamp, setTimestamp] = useSearchParam(`${props.side}Timestamp`);

    const contractIdMap = useMemo(() => createDealMap(props.deals), [props.deals]);

    const capitalizedName = props.side[0].toUpperCase() + props.side.slice(1);

    useEffect(() => {
        if (!isNil(id) && !isNaN(Number.parseInt(id))) {
            props.updateId(Number.parseInt(id));
        }
    }, [id]);

    useEffect(() => {
        props.updateTimestamp(timestamp);
    }, [timestamp]);

    return (
        <Column alignmentVertical={'top'} height={'100%'}>
            <Row widths={['fill', 'fit']}>
                <DataSelector
                    id={id}
                    setId={setId}
                    idMap={contractIdMap}
                    title={'Contract'}
                    prefix={`${capitalizedName} Contract`}
                />
                <Tooltip position={'end'} title={'View Contract'}>
                    <Button
                        aria-label={'view-contract'}
                        target={'_blank'}
                        href={`${ROUTES.RATE_CARDS.INDEX}/${id}`}
                        disabled={isNil(id)}
                    >
                        <Icon tokens={viewTokens} />
                    </Button>
                </Tooltip>
                <ContractSearchModal
                    entities={props.entities}
                    deals={props.deals}
                    contractIdMap={contractIdMap}
                    setContractId={setId}
                />
            </Row>
            <TimestampPicker timestamp={timestamp} setTimestamp={setTimestamp} />
        </Column>
    );
};

export function createDealMap(deals?: ContractDeal[]): Map<string, string> {
    const map = new Map<string, string>();
    deals?.forEach((deal) => map.set(`${deal.contractId}`, deal.contractName));
    return map;
}
